import { useState, useEffect } from 'react';
import { FolderOpen, Plus, Archive, Trash2, CreditCard as Edit2, X, Clock, FileText, CheckCircle, Lock, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getAllLocalProjects, purgeLocalProject, saveProjectLocally, clearLastProjectId, purgeOrphanedLocalProjects } from '../lib/localProjects';
import { useAuth } from '../contexts/AuthContext';
import type { ProjectStatus } from '../types/project';

interface ProjectListItem {
  id: string;
  project_name: string;
  status: ProjectStatus;
  customer_name: string;
  updated_at: string;
  last_opened_at: string;
  results_unlocked: boolean;
  isLocal?: boolean;
}

interface FirmProjectListItem {
  id: string;
  project_name: string;
  status: ProjectStatus;
  customer_name: string;
  updated_at: string;
  owner_name: string | null;
}

interface FirmSettings {
  project_visibility: string;
  project_edit_permission: string;
}

interface ProjectManagerProps {
  currentProjectId: string | null;
  onLoadProject: (projectId: string) => void;
  onLoadFirmProject?: (projectId: string, ownerName: string | null, editPermission: string) => void;
  onNewProject: () => void;
  onClose: () => void;
  onActiveProjectDeleted?: () => void;
}

export default function ProjectManager({
  currentProjectId,
  onLoadProject,
  onLoadFirmProject,
  onNewProject,
  onClose,
  onActiveProjectDeleted,
}: ProjectManagerProps) {
  const { user, userProfile } = useAuth();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [filterStatus, setFilterStatus] = useState<ProjectStatus | 'all'>('all');
  const [activeProjectTab, setActiveProjectTab] = useState<'mine' | 'firm'>('mine');
  const [firmSettings, setFirmSettings] = useState<FirmSettings | null>(null);
  const [firmProjects, setFirmProjects] = useState<FirmProjectListItem[]>([]);
  const [firmProjectsLoading, setFirmProjectsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);
  const [batchResult, setBatchResult] = useState<{ succeeded: number; failedNames: string[]; verb: string } | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Firm project access flows through the member policies alone: a seat plus
  // the firm's visibility setting. Firm admins have no special row access —
  // their project directory lives in the Firm Dashboard.
  const showFirmTab =
    !!userProfile?.firm_id &&
    !!userProfile?.has_seat &&
    firmSettings?.project_visibility === 'firm_all';

  const firmMemberReadOnly =
    firmSettings?.project_edit_permission === 'own_only';

  useEffect(() => {
    loadProjects();
  }, []);

  // A selection only makes sense within the list that produced it. Changing the
  // status filter or switching tabs invalidates it.
  useEffect(() => {
    setSelectedIds(new Set());
    setBatchResult(null);
  }, [filterStatus, activeProjectTab]);

  // Load firm settings when firm_id is available
  useEffect(() => {
    if (!userProfile?.firm_id) return;
    supabase
      .from('firms')
      .select('project_visibility, project_edit_permission')
      .eq('id', userProfile.firm_id)
      .single()
      .then(({ data }) => { if (data) setFirmSettings(data); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.firm_id]);

  // Load firm projects when firm tab is selected and settings are loaded
  useEffect(() => {
    if (activeProjectTab !== 'firm' || !showFirmTab || !user || !userProfile?.firm_id) return;
    loadFirmProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectTab, showFirmTab, userProfile?.firm_id]);

  async function loadFirmProjects() {
    setFirmProjectsLoading(true);
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, project_name, status, customer_name, updated_at, user_id')
        .eq('firm_id', userProfile!.firm_id)
        .neq('user_id', user!.id)
        .order('updated_at', { ascending: false });

      if (!error && data) {
        // projects.user_id has no FK to user_profiles (both reference auth.users),
        // so PostgREST cannot embed the owner profile. Resolve owner names with a
        // second query keyed on user_profiles.id, which is the auth user id.
        const ownerIds: string[] = Array.from(
          new Set(data.map(p => p.user_id).filter((id): id is string => typeof id === 'string'))
        );
        const ownerNames = new Map<string, string | null>();
        if (ownerIds.length > 0) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, full_name')
            .in('id', ownerIds);
          profiles?.forEach(profile => {
            ownerNames.set(profile.id, profile.full_name ?? null);
          });
        }

        setFirmProjects(data.map(p => ({
          id: p.id,
          project_name: p.project_name,
          status: p.status as ProjectStatus,
          customer_name: p.customer_name || '',
          updated_at: p.updated_at,
          owner_name: ownerNames.get(p.user_id) ?? null,
        })));
      }
    } catch (err) {
      console.error('Error loading firm projects:', err);
    } finally {
      setFirmProjectsLoading(false);
    }
  }

  async function loadProjects() {
    try {
      setLoading(true);
      const projectsMap = new Map<string, ProjectListItem>();

      const localProjects = await getAllLocalProjects();
      Object.values(localProjects).forEach((localProj) => {
        projectsMap.set(localProj.projectId, {
          id: localProj.projectId,
          project_name: localProj.project.project_name || 'Untitled Project',
          status: (localProj.project.status as ProjectStatus) || 'draft',
          customer_name: localProj.project.customer_name || '',
          updated_at: localProj.savedAt,
          last_opened_at: localProj.savedAt,
          results_unlocked: false,
          isLocal: true,
        });
      });

      let cloudFetchSucceeded = false;
      let cloudIdList: string[] = [];

      try {
        if (!user) throw new Error('No authenticated user');
        const { data, error } = await supabase
          .from('projects')
          .select('id, project_name, status, customer_name, updated_at, last_opened_at, results_unlocked')
          .eq('user_id', user.id)
          .order('last_opened_at', { ascending: false });

        if (!error && data) {
          data.forEach(p => {
            projectsMap.set(p.id, { ...p, results_unlocked: !!p.results_unlocked, isLocal: false });
          });
          cloudFetchSucceeded = true;
          cloudIdList = data.map(p => p.id);
        }
      } catch (cloudError) {
        console.log('Cloud projects not available');
      }

      // Reconcile deletions performed on this or another device. Only safe when the
      // cloud list is known-good — a failed fetch would make every synced project
      // look deleted. Do not remove this guard.
      if (cloudFetchSucceeded) {
        const purgedIds = await purgeOrphanedLocalProjects(new Set(cloudIdList));
        for (const id of purgedIds) {
          projectsMap.delete(id);
        }
      }

      // Defensive dedup: if a local_ entry and a cloud entry share the same
      // project_name + customer_name + approximate creation date, the local_
      // entry is a stale shadow of the promoted cloud record. Remove it.
      // This is a backstop only — the primary fix is in handleSave (App.tsx).
      const cloudIds = new Set(
        Array.from(projectsMap.values())
          .filter(p => !p.isLocal)
          .map(p => p.id)
      );
      for (const [key, p] of projectsMap.entries()) {
        if (p.isLocal && !cloudIds.has(key)) {
          // Check whether a cloud record with matching name exists
          const shadow = Array.from(projectsMap.values()).find(
            q => !q.isLocal && q.project_name === p.project_name
          );
          if (shadow) {
            projectsMap.delete(key);
          }
        }
      }

      const allProjects = Array.from(projectsMap.values());
      allProjects.sort((a, b) =>
        new Date(b.last_opened_at).getTime() - new Date(a.last_opened_at).getTime()
      );

      setProjects(allProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  }

  async function updateProjectStatus(projectId: string, status: ProjectStatus) {
    try {
      const project = projects.find(p => p.id === projectId);
      if (project?.isLocal) {
        const localProjects = await getAllLocalProjects();
        const localProj = localProjects[projectId];
        if (localProj) {
          const updatedProject = { ...localProj.project, status };
          await saveProjectLocally(projectId, updatedProject, localProj.equipment, localProj.amiData);
        }
      } else {
        const { error } = await supabase
          .from('projects')
          .update({ status, updated_at: new Date().toISOString() })
          .eq('id', projectId);

        if (error) throw error;
      }
      if (status === 'archived' && user?.id) {
        clearLastProjectId(user.id, projectId);
      }
      await loadProjects();
    } catch (error) {
      console.error('Error updating project status:', error);
    }
  }

  async function updateProjectName(projectId: string, newName: string) {
    try {
      const project = projects.find(p => p.id === projectId);
      if (project?.isLocal) {
        const localProjects = await getAllLocalProjects();
        const localProj = localProjects[projectId];
        if (localProj) {
          const updatedProject = { ...localProj.project, project_name: newName };
          await saveProjectLocally(projectId, updatedProject, localProj.equipment, localProj.amiData);
        }
      } else {
        const { error } = await supabase
          .from('projects')
          .update({ project_name: newName, updated_at: new Date().toISOString() })
          .eq('id', projectId);

        if (error) throw error;

        const localProjects = await getAllLocalProjects();
        const localProj = localProjects[projectId];
        if (localProj) {
          const updatedProject = { ...localProj.project, project_name: newName };
          await saveProjectLocally(projectId, updatedProject, localProj.equipment, localProj.amiData);
        }
      }

      window.dispatchEvent(new CustomEvent('projectNameUpdated', {
        detail: { projectId, projectName: newName }
      }));

      await loadProjects();
      setEditingId(null);
    } catch (error) {
      console.error('Error updating project name:', error);
    }
  }

  async function deleteProject(projectId: string) {
    if (!window.confirm('Are you sure you want to delete this project? Deleting will result in permanent loss of all project data.')) {
      return;
    }

    try {
      const project = projects.find(p => p.id === projectId);
      if (project?.isLocal) {
        await purgeLocalProject(projectId);
      } else{
        await supabase.from('ami_data_points').delete().eq('project_id', projectId);
        await supabase.from('equipment').delete().eq('project_id', projectId);
        const { error } = await supabase.from('projects').delete().eq('id', projectId);

        if (error) throw error;

        await purgeLocalProject(projectId);
      }
      if (user?.id) clearLastProjectId(user.id, projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      if (projectId === currentProjectId) {
        onActiveProjectDeleted?.();
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  }

  // Shared by the batch Archive and Restore actions. Tracks per-item outcomes so
  // the result banner reports what actually happened — a chunk whose write
  // errored is reported as failed, never silently counted as a success.
  async function runBatchStatus(targets: ProjectListItem[], status: ProjectStatus, verb: string) {
    setBatchRunning(true);
    setBatchResult(null);
    setBatchProgress({ done: 0, total: targets.length });

    const failedNames: string[] = [];
    const succeededIds: string[] = [];
    let done = 0;

    const localTargets = targets.filter(p => p.isLocal);
    const cloudTargets = targets.filter(p => !p.isLocal);

    for (const project of localTargets) {
      try {
        const localProjects = await getAllLocalProjects();
        const localProj = localProjects[project.id];
        if (localProj) {
          const updatedProject = { ...localProj.project, status };
          await saveProjectLocally(project.id, updatedProject, localProj.equipment, localProj.amiData);
        }
        succeededIds.push(project.id);
      } catch (error) {
        console.error('Error updating local project status:', error);
        failedNames.push(project.project_name);
      }
      done += 1;
      setBatchProgress({ done, total: targets.length });
    }

    const CHUNK_SIZE = 25;
    for (let i = 0; i < cloudTargets.length; i += CHUNK_SIZE) {
      const chunk = cloudTargets.slice(i, i + CHUNK_SIZE);
      const ids = chunk.map(p => p.id);
      try {
        const { error } = await supabase
          .from('projects')
          .update({ status, updated_at: new Date().toISOString() })
          .in('id', ids);

        if (error) throw error;
        succeededIds.push(...ids);
      } catch (error) {
        console.error('Error updating project status batch:', error);
        chunk.forEach(p => failedNames.push(p.project_name));
      }
      done += chunk.length;
      setBatchProgress({ done, total: targets.length });
    }

    const userId = user?.id;
    if (status === 'archived' && userId) {
      succeededIds.forEach(id => clearLastProjectId(userId, id));
    }

    setBatchProgress(null);
    setBatchRunning(false);
    setSelectedIds(new Set());
    setBatchResult({ succeeded: succeededIds.length, failedNames, verb });
    await loadProjects();
  }

  async function runBatchDelete(targets: ProjectListItem[]) {
    setBatchRunning(true);
    setBatchResult(null);
    setBatchProgress({ done: 0, total: targets.length });

    const failedNames: string[] = [];
    const succeededIds: string[] = [];
    let done = 0;

    const localTargets = targets.filter(p => p.isLocal);
    const cloudTargets = targets.filter(p => !p.isLocal);

    for (const project of localTargets) {
      try {
        await purgeLocalProject(project.id);
        succeededIds.push(project.id);
      } catch (error) {
        console.error('Error deleting local project:', error);
        failedNames.push(project.project_name);
      }
      done += 1;
      setBatchProgress({ done, total: targets.length });
    }

    // Deliberately small: AMI row counts per project can be very large, and a
    // wide DELETE has exhausted production disk IO before. A failed chunk is
    // recorded and skipped — it does not abort the remaining chunks.
    const CHUNK_SIZE = 5;
    for (let i = 0; i < cloudTargets.length; i += CHUNK_SIZE) {
      const chunk = cloudTargets.slice(i, i + CHUNK_SIZE);
      const ids = chunk.map(p => p.id);
      try {
        const amiResult = await supabase.from('ami_data_points').delete().in('project_id', ids);
        if (amiResult.error) throw amiResult.error;

        const equipmentResult = await supabase.from('equipment').delete().in('project_id', ids);
        if (equipmentResult.error) throw equipmentResult.error;

        const projectResult = await supabase.from('projects').delete().in('id', ids);
        if (projectResult.error) throw projectResult.error;

        succeededIds.push(...ids);
      } catch (error) {
        console.error('Error deleting project batch:', error);
        chunk.forEach(p => failedNames.push(p.project_name));
      }
      done += chunk.length;
      setBatchProgress({ done, total: targets.length });
    }

    // A cloud project also has a localStorage copy filed under the same UUID and
    // an IndexedDB AMI record. Without this the row is rebuilt from browser
    // storage on the next load and appears not to have been deleted at all.
    for (const id of succeededIds) {
      if (id.startsWith('local_')) continue;   // already handled above
      try {
        await purgeLocalProject(id);
      } catch (error) {
        console.error('Error clearing local copy of deleted project:', error);
      }
    }

    const userId = user?.id;
    if (userId) {
      succeededIds.forEach(id => clearLastProjectId(userId, id));
    }

    const activeProjectWasDeleted = !!currentProjectId && succeededIds.includes(currentProjectId);

    setBatchProgress(null);
    setBatchRunning(false);
    setSelectedIds(new Set());
    setBatchResult({ succeeded: succeededIds.length, failedNames, verb: 'Deleted' });

    if (activeProjectWasDeleted) {
      onActiveProjectDeleted?.();
    }

    await loadProjects();
  }

  function startEdit(project: ProjectListItem) {
    setEditingId(project.id);
    setEditName(project.project_name);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName('');
  }

  const filteredProjects = projects.filter(
    (p) => filterStatus === 'all' || p.status === filterStatus
  );

  const selectedProjects = filteredProjects.filter(p => selectedIds.has(p.id));
  const archivableSelected = selectedProjects.filter(p => p.status !== 'archived');
  const restorableSelected = selectedProjects.filter(p => p.status === 'archived');
  const unlockedSelectedCount = selectedProjects.filter(p => p.results_unlocked).length;
  const allVisibleSelected = filteredProjects.length > 0 && filteredProjects.every(p => selectedIds.has(p.id));

  const statusColors: Record<ProjectStatus, string> = {
    draft: 'bg-gray-100 text-gray-700',
    'in-progress': 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    archived: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div data-testid="project-manager-modal" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div data-testid="project-manager-panel" className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <FolderOpen className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Project Manager</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="btn-project-manager-close"
            className="text-gray-400 hover:text-gray-600 transition-colors touch-manipulation p-2"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {showFirmTab && (
          <div className="px-3 sm:px-6 border-b border-gray-200 flex">
            <button
              data-testid="tab-my-projects"
              onClick={() => setActiveProjectTab('mine')}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeProjectTab === 'mine'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Projects
            </button>
            <button
              data-testid="tab-firm-projects"
              onClick={() => setActiveProjectTab('firm')}
              className={`py-3 px-4 text-sm font-medium border-b-2 flex items-center gap-1.5 transition-colors ${
                activeProjectTab === 'firm'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              Firm Projects
            </button>
          </div>
        )}

        <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
          {activeProjectTab === 'mine' && filteredProjects.length > 0 && (
            <label className="flex items-center gap-2 flex-shrink-0 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="btn-select-all"
                checked={allVisibleSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedIds(new Set(filteredProjects.map(p => p.id)));
                  } else {
                    setSelectedIds(new Set());
                  }
                }}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-xs sm:text-sm text-gray-700 whitespace-nowrap">Select all</span>
            </label>
          )}
          <div data-testid="project-status-filter" className="flex space-x-1 sm:space-x-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap touch-manipulation ${
                filterStatus === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Projects
            </button>
            <button
              onClick={() => setFilterStatus('in-progress')}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap touch-manipulation ${
                filterStatus === 'in-progress'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              In Progress
            </button>
            <button
              onClick={() => setFilterStatus('completed')}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap touch-manipulation ${
                filterStatus === 'completed'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completed
            </button>
            <button
              onClick={() => setFilterStatus('archived')}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap touch-manipulation ${
                filterStatus === 'archived'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Archived
            </button>
          </div>
          </div>
          <button
            onClick={() => {
              onNewProject();
              onClose();
            }}
            data-testid="btn-new-project"
            className="flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm sm:text-base touch-manipulation"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>New Project</span>
          </button>
        </div>

        {activeProjectTab === 'mine' && selectedIds.size > 0 && (
          <div
            data-testid="batch-action-bar"
            className="bg-blue-50 border-b border-blue-200 px-3 sm:px-6 py-2 flex flex-wrap items-center gap-2"
          >
            <span className="font-medium text-sm text-blue-900">{selectedIds.size} selected</span>
            <button
              data-testid="btn-batch-archive"
              disabled={batchRunning || archivableSelected.length === 0}
              onClick={() => runBatchStatus(archivableSelected, 'archived', 'Archived')}
              className="px-3 py-1.5 text-sm rounded-md bg-yellow-600 text-white hover:bg-yellow-700 disabled:bg-gray-200 disabled:text-gray-400"
            >
              Archive ({archivableSelected.length})
            </button>
            <button
              data-testid="btn-batch-restore"
              disabled={batchRunning || restorableSelected.length === 0}
              onClick={() => runBatchStatus(restorableSelected, 'in-progress', 'Restored')}
              className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400"
            >
              Restore ({restorableSelected.length})
            </button>
            <button
              data-testid="btn-batch-delete"
              disabled={batchRunning || selectedIds.size === 0}
              onClick={() => setConfirmDeleteOpen(true)}
              className="px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400"
            >
              Delete ({selectedIds.size})
            </button>
            <button
              data-testid="btn-batch-clear"
              disabled={batchRunning}
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-gray-600 underline"
            >
              Clear
            </button>
            {batchProgress && (
              <span className="text-sm text-blue-900">
                Processing {batchProgress.done} of {batchProgress.total}...
              </span>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          {batchResult && (
            <div
              data-testid="batch-result-banner"
              className={`mb-4 flex items-start justify-between gap-3 border rounded-lg px-4 py-3 text-sm ${
                batchResult.failedNames.length === 0
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-amber-50 border-amber-200 text-amber-900'
              }`}
            >
              <div className="min-w-0">
                {batchResult.failedNames.length === 0 ? (
                  <p>{batchResult.verb} {batchResult.succeeded} project(s).</p>
                ) : (
                  <>
                    <p>
                      {batchResult.verb} {batchResult.succeeded} of{' '}
                      {batchResult.succeeded + batchResult.failedNames.length} projects. Failed:{' '}
                      {batchResult.failedNames.join(', ')}
                    </p>
                    <p className="mt-1">
                      These projects were not changed. Try again, or contact support if the problem repeats.
                    </p>
                  </>
                )}
              </div>
              <button
                onClick={() => setBatchResult(null)}
                title="Dismiss"
                className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {activeProjectTab === 'firm' ? (
            <div data-testid="firm-projects-list">
              {firmMemberReadOnly && (
                <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                  <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">You can view and generate reports on these projects but cannot edit them.</p>
                </div>
              )}
              {firmProjectsLoading ? (
                <div className="text-center py-8 text-sm text-gray-500">Loading firm projects...</div>
              ) : firmProjects.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">No other firm projects found.</div>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {firmProjects.map(fp => (
                    <div
                      key={fp.id}
                      onClick={() => { (onLoadFirmProject ?? onLoadProject)(fp.id, fp.owner_name ?? null, firmSettings?.project_edit_permission ?? 'own_only'); }}
                      className="border border-gray-200 hover:border-blue-400 hover:bg-blue-50 bg-white rounded-lg p-4 transition-all cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-base font-semibold text-gray-900 break-words flex-1 min-w-0">
                              {fp.project_name}
                            </h3>
                            {firmMemberReadOnly && (
                              <span title="View only" className="inline-flex flex-shrink-0">
                                <Lock className="w-3.5 h-3.5 text-gray-400" />
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${statusColors[fp.status]}`}>
                              {fp.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                              <span>{fp.status === 'in-progress' ? 'In Progress' : fp.status.charAt(0).toUpperCase() + fp.status.slice(1)}</span>
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">Customer: {fp.customer_name || 'Not specified'}</p>
                          {fp.owner_name && (
                            <p className="text-xs text-gray-500">by {fp.owner_name}</p>
                          )}
                          <div className="flex items-center space-x-1 text-xs text-gray-500 mt-1">
                            <Clock className="w-3 h-3" />
                            <span>Updated: {new Date(fp.updated_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={(e) => { e.stopPropagation(); (onLoadFirmProject ?? onLoadProject)(fp.id, fp.owner_name ?? null, firmSettings?.project_edit_permission ?? 'own_only'); }}
                            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                          >
                            Open
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : loading ? (
            <div className="text-center py-8 text-sm sm:text-base text-gray-500">Loading projects...</div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-8 text-sm sm:text-base text-gray-500">
              {filterStatus === 'all'
                ? 'No projects yet. Create your first project!'
                : `No ${filterStatus === 'in-progress' ? 'in progress' : filterStatus} projects.`}
            </div>
          ) : (
            <div data-testid="project-list" className="space-y-2 sm:space-y-3">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  data-testid={`project-item-${project.id}`}
                  onClick={() => {
                    if (editingId === project.id) return;
                    if (project.id === currentProjectId) {
                      onClose();
                    } else {
                      onLoadProject(project.id);
                    }
                  }}
                  className={`border rounded-lg p-4 transition-all cursor-pointer ${
                    project.id === currentProjectId
                      ? 'border-blue-500 bg-blue-50 hover:bg-blue-100'
                      : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <input
                      type="checkbox"
                      data-testid={`project-select-${project.id}`}
                      checked={selectedIds.has(project.id)}
                      disabled={batchRunning}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        setSelectedIds(prev => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(project.id); else next.delete(project.id);
                          return next;
                        });
                      }}
                      className="mt-1 mr-3 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      {editingId === project.id ? (
                        <div className="mb-2" onClick={(e) => e.stopPropagation()}>
                          <label className="block text-xs font-medium text-blue-700 mb-1">
                            Rename project
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && editName.trim()) updateProjectName(project.id, editName.trim());
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              className="flex-1 px-3 py-1.5 border-2 border-blue-400 rounded-md focus:outline-none focus:border-blue-600 text-sm"
                              autoFocus
                            />
                            <button
                              onClick={() => { if (editName.trim()) updateProjectName(project.id, editName.trim()); }}
                              disabled={!editName.trim()}
                              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-2">
                          <div className="flex items-start gap-2">
                            <h3 className="text-base font-semibold text-gray-900 break-words flex-1 min-w-0">
                              {project.project_name}
                            </h3>
                            <button
                              onClick={(e) => { e.stopPropagation(); startEdit(project); }}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0 mt-0.5"
                              title="Rename this project"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                              <span>Rename</span>
                            </button>
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
                                statusColors[project.status]
                              }`}
                            >
                              {project.status === 'completed' && <CheckCircle className="w-3 h-3" />}
                              <span>
                                {project.status === 'in-progress'
                                  ? 'In Progress'
                                  : project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                              </span>
                            </span>
                            {project.id === currentProjectId && (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-600 text-white">
                                Active
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      <p className="text-sm text-gray-600 mb-1">
                        Customer: {project.customer_name || 'Not specified'}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>Updated: {new Date(project.updated_at).toLocaleDateString()}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <FileText className="w-3 h-3" />
                          <span>Opened: {new Date(project.last_opened_at).toLocaleDateString()}</span>
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {project.id !== currentProjectId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onLoadProject(project.id); }}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                        >
                          Open
                        </button>
                      )}
                      {project.status !== 'archived' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); updateProjectStatus(project.id, 'archived'); }}
                          className="p-2 text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                          title="Archive"
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                      )}
                      {project.status === 'archived' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); updateProjectStatus(project.id, 'in-progress'); }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Restore to Active"
                        >
                          <FolderOpen className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {confirmDeleteOpen && (
        <div
          data-testid="batch-delete-confirm-modal"
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Delete {selectedProjects.length} project(s)?
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              This permanently deletes these projects and all of their AMI data, equipment, and
              results. This cannot be undone.
            </p>
            {unlockedSelectedCount > 0 && (
              <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">
                {unlockedSelectedCount} of these project(s) have already been paid for and unlocked.
                Deleting them does not refund the charge, and rebuilding the same project later will
                require paying again.
              </div>
            )}
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded text-sm mb-4">
              {selectedProjects.map(p => (
                <div key={p.id} className="px-3 py-1.5 text-gray-700 break-words">
                  {p.project_name}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                data-testid="btn-batch-delete-cancel"
                onClick={() => setConfirmDeleteOpen(false)}
                className="px-3 py-1.5 border border-gray-300 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="btn-batch-delete-confirm"
                onClick={() => {
                  setConfirmDeleteOpen(false);
                  runBatchDelete(selectedProjects);
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Delete {selectedProjects.length} project(s)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
